// =========================================================================
// Dataset → source-drawer payload. Issues module, fork-owned (zone C).
//
// The source demo answers "where did this number come from" with `audit(key,
// filter)` (line ~1907), which reads a row set out of its in-memory bundle
// `B`, applies a filter (a value, a list, or a predicate) and renders the
// slice with its file path and row count. The port's filters are declarative —
// see `shared/row-filter.ts`. The table behind it is `const SRC = {…}` at line 1875 — one entry
// per connected system, carrying a display title, the file path inside
// `meridian-data/`, its wire format and its column order.
//
// This module is the ported subset of that table: the datasets the Issues,
// Fleet and Service surfaces cite, with the demo's titles, paths, formats and
// column orders verbatim. Rows come from the lazy domain files (`useOemData`'s
// loaders) or, for the three that ship in the eager core bundle, straight
// from `data/core.ts`.
//
// It is **not** a transform — `loadDatasetRows` fetches — so it lives beside
// the scenes rather than in a `.transform.ts`. The pure half (column order,
// provenance string) is exported separately and has no I/O.
//
// Adding a dataset: copy its row out of the demo's `SRC` table. Do not invent
// a title, path or column order; they are what makes the drawer credible.
// =========================================================================

import type { SourceTablePayload } from '@/versions/v1/chrome-oem/SourceDrawerBody';
import { COST_PER_MACHINE, ENERGY_FACTORS, HEALTH } from '@/versions/v1/data/core';
import { unpackTable } from '@/versions/v1/shared/columnar';
import {
  describeFilter,
  filterRows,
  matchingIndexes,
  type RowFilter,
} from '@/versions/v1/shared/row-filter';
import { loadOemDomain } from '@/versions/v1/shared/useOemData';
import type {
  DailyMetricRow,
  EventDailyRow,
  ShiftDailyRow,
  WorkOrder,
} from '@/versions/v1/shared/useOemData';

/** A dataset row, erased to a plain record so one renderer covers them all. */
type Row = Record<string, unknown>;

/** The dataset keys carried by `AlertEvidenceRef` and `IncidentFigureSource`. */
export type DatasetKey =
  | 'wos'
  | 'health'
  | 'shift'
  | 'metric'
  | 'events'
  | 'cases'
  | 'live'
  | 'rmstq'
  | 'efac'
  | 'ledger_m'
  | 'cpm';

type DatasetSpec = {
  /** Drawer title — the demo's `SRC[key].title`. */
  readonly title: string;
  /** Path inside `meridian-data/` — the demo's `SRC[key].path`. */
  readonly path: string;
  /** Wire format tag, e.g. `'csv'`, `'jsonl'`. */
  readonly fmt: string;
  /** Column order, verbatim from the demo. */
  readonly cols: readonly string[];
  /** Fetches (or reads) the whole row set. */
  readonly load: () => Promise<Row[]>;
};

/** The demo's `SRC` table, narrowed to the datasets this module cites. */
const DATASETS: Readonly<Record<DatasetKey, DatasetSpec>> = {
  wos: {
    title: 'Work orders',
    path: '02_sap_pm/work_orders.csv',
    fmt: 'csv',
    cols: [
      'wo_id',
      'mine',
      'machine_id',
      'wo_type',
      'pm_code',
      'status',
      'created',
      'component',
      'downtime_h',
      'labour_h',
      'crew',
      'labour_cost_aud',
      'parts_cost_aud',
      'freight_cost_aud',
      'external_cost_aud',
      'downtime_cost_aud',
      'total_direct_cost_aud',
      'root_cause',
    ],
    load: async () => {
      const payload = await loadOemDomain('work-orders');
      return unpackTable<WorkOrder>(payload.wos) as unknown as Row[];
    },
  },

  health: {
    title: 'Component condition register',
    path: '00_fleet/component_health.csv',
    fmt: 'csv',
    cols: ['machine_id', 'component', 'health_score', 'status', 'hours_since_change', 'note'],
    // Eager — the condition register ships in `data/core.json`.
    load: async () => HEALTH as unknown as Row[],
  },

  shift: {
    title: 'Shift records (daily rollup)',
    path: '01_telematics/shift_end/shift_records_all_mines.csv',
    fmt: 'csv',
    cols: ['machine_id', 'date', 'hours', 'alarms', 'abuse', 'diesel_l', 'kwh', 'co2_kg', 'delays'],
    load: async () => {
      const payload = await loadOemDomain('telemetry');
      return unpackTable<ShiftDailyRow>(payload.shift_daily) as unknown as Row[];
    },
  },

  metric: {
    title: 'Daily key metric',
    path: '01_telematics/daily_key_metric_all_machines.csv',
    fmt: 'csv',
    cols: ['machine_id', 'date', 'metric', 'value', 'utilisation_pct'],
    load: async () => {
      const payload = await loadOemDomain('telemetry');
      return unpackTable<DailyMetricRow>(payload.daily_metric) as unknown as Row[];
    },
  },

  events: {
    title: 'Fault / alarm events (daily counts)',
    path: '01_telematics/fault_events.csv',
    fmt: 'csv',
    cols: ['machine_id', 'day', 'code', 'severity', 'n'],
    load: async () => {
      const payload = await loadOemDomain('telemetry');
      return unpackTable<EventDailyRow>(payload.events_daily) as unknown as Row[];
    },
  },

  cases: {
    title: 'Service cases',
    path: '03_parts_salesforce/service_cases.csv',
    fmt: 'csv',
    cols: ['case_no', 'opened', 'mine', 'machine_id', 'type', 'status', 'owner', 'subject'],
    load: async () => {
      const payload = await loadOemDomain('service');
      return payload.cases as unknown as Row[];
    },
  },

  live: {
    title: 'Live telematics (3-min sample)',
    path: '01_telematics/live_mqtt/*.jsonl',
    fmt: 'jsonl',
    cols: [
      'machine_id',
      'ts',
      'state',
      'speed_kmh',
      'brake_temp_rr_c',
      'retarder_pct',
      'intake_air_c',
      'engine_derate_pct',
      'video_link_quality',
      'alarm',
    ],
    load: async () => {
      const payload = await loadOemDomain('telemetry-live');
      // The registry is keyed by machine; the demo flattens it and stamps the
      // key onto every sample so `machine_id` can be filtered like any column.
      return Object.entries(payload.live).flatMap(([machineId, samples]) =>
        samples.map((sample) => ({ machine_id: machineId, ...sample }) as Row),
      );
    },
  },

  rmstq: {
    title: 'RMS make-up / breakout torque log · S-05 · daily mean',
    path: '15_rms/rms_torque_log_S-05_412B.csv',
    fmt: 'csv',
    cols: ['machine_id', 'date', 'joints', 'makeup_knm', 'breakout_knm', 'high'],
    load: async () => {
      const payload = await loadOemDomain('rms');
      // The file is one machine's log and carries no id column; the demo adds
      // the literal so the filter shape stays uniform.
      return payload.rms_torque_s05.map((row) => ({ machine_id: 'S-05', ...row }) as Row);
    },
  },

  efac: {
    title: 'Energy factors',
    path: '09_costs/energy_factors.csv',
    fmt: 'csv',
    cols: [
      'mine',
      'power_source',
      'grid_kgco2_per_kwh',
      'diesel_kgco2_per_l',
      'diesel_aud_per_l',
      'power_aud_per_kwh',
    ],
    // Eager — the rate card ships in `data/core.json`.
    load: async () => ENERGY_FACTORS as unknown as Row[],
  },

  ledger_m: {
    title: 'Cost ledger · by machine and category',
    path: '09_costs/cost_ledger.csv (aggregated)',
    fmt: 'csv',
    cols: ['machine_id', 'category', 'amount_aud'],
    load: async () => {
      const payload = await loadOemDomain('costs');
      return payload.cost_by_machine_cat as unknown as Row[];
    },
  },

  cpm: {
    title: 'Cost per operating hour',
    path: '09_costs/cost_per_machine.csv',
    fmt: 'csv',
    cols: ['machine_id', 'amount_aud', 'op_hours_18m', 'cost_per_op_hour_aud'],
    // Eager — the per-machine roll-up ships in `data/core.json`.
    load: async () => COST_PER_MACHINE as unknown as Row[],
  },
};

/** `true` when this module can resolve the dataset key to real rows. */
export function isKnownDataset(key: string): key is DatasetKey {
  return Object.prototype.hasOwnProperty.call(DATASETS, key);
}

/** The demo's display title for a dataset, or the raw key when unported. */
export function datasetTitle(key: string): string {
  return isKnownDataset(key) ? DATASETS[key].title : key;
}

/**
 * The demo's `#dr-path` line: where the file lives, how much of it matched
 * and what narrowed it.
 *
 * Pure — the caller supplies the counts.
 */
export function datasetProvenance(
  key: DatasetKey,
  matched: number,
  total: number,
  filter: RowFilter,
): string {
  const spec = DATASETS[key];
  const filterText = Object.keys(filter).length ? ` · filter: ${describeFilter(filter)}` : '';
  return `meridian-data/${spec.path} · ${spec.fmt.toUpperCase()} · ${matched} of ${total} rows${filterText}`;
}

/** Coerce a row value into something the source drawer can print. */
function cell(value: unknown): string | number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(' ');
  if (typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** What narrows and marks a dataset slice. */
export interface DatasetQuery {
  /** Rows kept — exact match, any-of, or a declarative range; see `row-filter.ts`. */
  readonly filter?: RowFilter;
  /** Rows marked among those kept — the demo's `opts.hl`. */
  readonly highlight?: RowFilter;
}

/**
 * Load a dataset, apply the demo's filter and shape the result for the
 * `oem-source` drawer.
 *
 * Every matched row goes on the payload; the drawer renders the first 400 and
 * says so, and its "Filter rows…" box searches the rest.
 *
 * @param key   a dataset key off an `AlertEvidenceRef` or `IncidentFigureSource`
 * @param query filter and highlight, e.g. `{ filter: { machine_id: 'T-09' } }`
 * @returns the drawer payload, or `null` when the key has no ported spec —
 *   the caller then falls back to showing the raw reference rather than
 *   opening an empty drawer.
 */
export async function loadDatasetRows(
  key: string,
  query: DatasetQuery = {},
): Promise<SourceTablePayload | null> {
  if (!isKnownDataset(key)) return null;
  const spec = DATASETS[key];
  const all = await spec.load();
  const filter = query.filter ?? {};
  const matched = filterRows(all, filter);
  const highlight = matchingIndexes(matched, query.highlight);

  return {
    kind: 'table',
    title: spec.title,
    columns: [...spec.cols],
    rows: matched.map((row) => spec.cols.map((col) => cell(row[col]))),
    provenance: datasetProvenance(key, matched.length, all.length, filter),
    ...(highlight.length ? { highlight } : {}),
  };
}
